import type {
    ChildTextContentElement,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import {
    useOptionGroupStyles,
    useTreeViewStyles,
} from 'components/WorkspaceEditor/styles'
import {
    ADDITIONAL_ELEMENT_SUFFIX,
    getStatusClass,
} from 'components/WorkspaceEditor/utils'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo, useCallback, useMemo } from 'react'
import { EmptyPlainTextContainer } from './EmptyPlainTextContainer'
import { PlainText } from './PlainText/PlainText'
import { ReplacementOption } from './ReplacementOption'
import { SelectionOption } from './SelectionOption'

/**
 * Filters and returns valid elements from a content item.
 * An element is considered valid if it has non-empty text or contains child elements.
 */
const getValidElements = (
    contentItem: Partial<TextContentElement>,
    isInactiveContentVisible: boolean
): ChildTextContentElement[] => {
    if (!contentItem.elements) return []

    return contentItem.elements.filter(
        (e): e is ChildTextContentElement =>
            e !== null &&
            typeof e === 'object' &&
            (e.text !== ' ' ||
                (Array.isArray(e.elements) && e.elements.length > 0)) &&
            // Filter out inactive options when visibility setting is off
            (isInactiveContentVisible || e.optionItemState !== 'InActive')
    )
}

/** Props for the OptionGroup component. */
interface OptionGroupProps {
    /** The content item to render */
    contentItem: Partial<TextContentElement>
    /** Flag that indicates whether the SelectionOptions are inactive */
    inactive?: boolean
    /** Whether the component is disabled (not hidden, just styled as disabled) */
    disabled?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** function to select/deselect pill */
    handlePillClick?: (
        currentSelectedItem: Partial<TextContentElement>,
        currentlySelected: boolean,
        currentContentItem: Partial<TextContentElement>,
        multipleSelectionType?: string
    ) => void
}

/**
 * OptionGroup component Renders an option group of either SelectionOption or ReplacementOption
 * @param {OptionGroupProps} props - The properties for the OptionGroup component.
 * @returns {JSX.Element | null} The rendered option group or null if no valid elements are found.
 */
export const OptionGroup = memo<OptionGroupProps>(
    ({ contentItem, inactive = false, disabled = false, editable = false, handlePillClick }) => {
        const { optionGroupContainer } = useOptionGroupStyles()
        const {
            paragraphTypography,
            inactiveText,
            addedText,
            removedText,
        } = useTreeViewStyles()
        const { areTrackChangesVisible, isInactiveContentVisible } = useShallowEditorActionBar(
            (state) => ({
                areTrackChangesVisible: state.visibilitySettingValues.areTrackChangesVisible,
                isInactiveContentVisible: state.visibilitySettingValues.isInactiveContentVisible,
            })
        )
        const elements = useMemo(
            () => getValidElements(contentItem, isInactiveContentVisible),
            [contentItem, isInactiveContentVisible]
        )

        /**
         * Type guard to determine if an element should render as a pill (SelectionOption).
         */
        const shouldRenderPill = useCallback(
            (element: Partial<ChildTextContentElement>): boolean =>
                element.type === NODE_TYPES.SelectionOption ||
                (element.type === NODE_TYPES.ReplacementOption &&
                    !!element.optionItemState &&
                    !element.placeholderText?.includes('Insert manufacturer')),
            []
        )

        /**
         * Type guard to determine if an element is a valid text element.
         */
        const isValidTextElement = useCallback(
            (element: Partial<ChildTextContentElement>): boolean =>
                element.type === NODE_TYPES.Text,
            []
        )

        const handleOptionGroupPillClick = useCallback(
            (
                currentSelectedItem: Partial<TextContentElement>,
                currentlySelected: boolean
            ) => {
                handlePillClick?.(
                    currentSelectedItem,
                    currentlySelected,
                    contentItem
                )
            },
            [contentItem, handlePillClick]
        )

        const generateElementKey = useCallback(
            (element: ChildTextContentElement, index: number) => {
                return `${contentItem.id}-${element.id || `idx-${index}`}`
            },
            [contentItem.id]
        )

        /**
         * Determines if an empty text container should be shown between pills
         * @param index - Current element index
         * @param elements - Array of all elements
         * @param totalElementsIndex - Total number of elements minus 1 (last valid index)
         * @param inactive - Whether the content is inactive
         * @returns True if empty container should be rendered
         */
        const shouldShowEmptyContainer = (
            index: number,
            elements: ChildTextContentElement[],
            totalElementsIndex: number,
            inactive: boolean
        ): boolean => {
            return (
                totalElementsIndex > index &&
                !isValidTextElement(elements[index + 1]) &&
                !inactive
            )
        }

        /**
         * Renders a single element in the option group
         * @param element - The element to render
         * @param index - Current element index
         * @param elements - Array of all elements
         * @param totalElementsIndex - Total number of elements minus 1 (last valid index)
         */
        const renderElement = (
            element: ChildTextContentElement,
            index: number,
            elements: ChildTextContentElement[],
            totalElementsIndex: number
        ) => {
            if (shouldRenderPill(element)) {
                const hasEmptyContainer = shouldShowEmptyContainer(
                    index,
                    elements,
                    totalElementsIndex,
                    inactive
                )
                return (
                    <>
                        <SelectionOption
                            key={`selection-${element.id || index}`}
                            contentItem={element}
                            parentContentItemId={contentItem.id}
                            inactive={inactive}
                            disabled={disabled}
                            editable={editable}
                            hasEmptyContainer={hasEmptyContainer}
                            onSelectionOptionClick={handleOptionGroupPillClick}
                        />
                        {hasEmptyContainer && (
                            <EmptyPlainTextContainer
                                element={{
                                    id: `${element.id}${ADDITIONAL_ELEMENT_SUFFIX}`,
                                    text: '',
                                    type: 'TEXT',
                                }}
                                inactive={inactive}
                                editable={editable}
                            />
                        )}
                    </>
                )
            }

            if (isValidTextElement(element)) {
                const changeType = element.changeType?.toUpperCase() ?? ''
                const statusClass = getStatusClass(
                    inactive,
                    changeType,
                    areTrackChangesVisible,
                    {
                        inactiveText,
                        addedText,
                        removedText,
                    }
                )
                return (
                    <PlainText
                        contentItem={element}
                        inactive={inactive}
                        editable={editable}
                        classes={clsx(paragraphTypography, statusClass)}
                    />
                )
            }

            // Default case
            return (
                <ReplacementOption
                    selector={`replacement-${element.id || index}`}
                    contentItem={element}
                    inactive={inactive}
                    disabled={disabled}
                    editable={editable}
                    onValueChange={handleOptionGroupPillClick}
                />
            )
        }

        // Guard clauses moved here after all hooks to comply with Rules of Hooks
        if (!contentItem?.id) {
            return null
        }

        if (elements.length === 0) {
            return null
        }

        return (
            <>
                {elements.map((element, index) => {
                    const isReplacementOptions =
                        !shouldRenderPill(element) &&
                        !isValidTextElement(element)
                    return (
                        <div
                            key={generateElementKey(element, index)}
                            data-testid={`option-group-container-${contentItem.id}`}
                            className={
                                (!isReplacementOptions &&
                                    optionGroupContainer) ||
                                ''
                            }
                        >
                            {renderElement(
                                element,
                                index,
                                elements,
                                elements.length - 1
                            )}
                        </div>
                    )
                })}
            </>
        )
    }
)
